import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, DefaultHttpStack } from "tus-js-client";
import { useNavigate, useLocation } from "react-router-dom";

const EditQuestionForm = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const questionId = searchParams.get("id");

  // Question fields
  const [qid, setQid] = useState(0);
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState(["", "", "", "", "", ""]);
  const [words, setWords] = useState(["", "", ""]);
  const [answer, setAnswer] = useState("");
  const [partsOfSpeech, setPartsOfSpeech] = useState("");
  const [hints, setHints] = useState("");
  const [priority, setPriority] = useState(0);
  const [otherInfo, setOtherInfo] = useState("");
  const [showWords, setShowWords] = useState(false);
  const [originalVideoUrl, setOriginalVideoUrl] = useState("");

  // Video upload fields
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [isVideoChanged, setIsVideoChanged] = useState(false);
  const navigate = useNavigate();

  // Tenses
  const [tenses, setTenses] = useState([]);
  const [selectedTense, setSelectedTense] = useState("");
  const [loadingTenses, setLoadingTenses] = useState(false);

  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingCategories(true);
      setLoadingTenses(true);
      setLoadingQuestion(true);

      try {
        const [categoriesRes, tensesRes, questionRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/fetch-categories?taxonomy=Quiz`
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/api/tenses`),
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/questions/${questionId}`
          ),
        ]);

        setCategories(categoriesRes.data);
        setTenses(tensesRes.data);

        // Set question data
        const questionData = questionRes.data;
        setQid(questionData.qid || 0);
        setQuestionText(questionData.question_text || "");
        setOptions([
          questionData.option_1 || "",
          questionData.option_2 || "",
          questionData.option_3 || "",
          questionData.option_4 || "",
          questionData.option_5 || "",
          questionData.option_6 || "",
        ]);
        setWords([
          questionData.word_1 || "",
          questionData.word_2 || "",
          questionData.word_3 || "",
        ]);
        setAnswer(questionData.answer || "");
        setPartsOfSpeech(questionData.parts_of_speech || "");
        setHints(questionData.hints || "");
        setPriority(questionData.priority || 0);
        setOtherInfo(questionData.other_info || "");
        setSelectedTense(questionData.tense || "");
        setOriginalVideoUrl(questionData.video_link || "");
        setVideoUrl(questionData.video_link || "");

        // Fetch and set selected categories
        const categoriesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/questions/${questionId}/categories`
        );
        const selectedCats = {};
        categoriesResponse.data.forEach((cat) => {
          selectedCats[cat.id] = true;
        });
        setSelectedCategories(selectedCats);
      } catch (err) {
        setError("Failed to load data");
        console.error(err);
      } finally {
        setLoadingCategories(false);
        setLoadingTenses(false);
        setLoadingQuestion(false);
      }
    };

    if (questionId) {
      fetchData();
    }
  }, [questionId]);

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
      setIsVideoChanged(true);
      setVideoUrl("");
      setError(null);
    }
  };

  const toggleExpand = (catID) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catID]: !prev[catID],
    }));
  };

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleWordChange = (index, value) => {
    const newWords = [...words];
    newWords[index] = value;
    setWords(newWords);
  };

  const handleUpdate = async () => {
    if (!questionText) {
      setError("Please enter a question.");
      return;
    }
    if (!partsOfSpeech) {
      setError("Please enter parts of speech.");
      return;
    }
    if (!answer) {
      setError("Please select answer.");
      return;
    }
    const filledOptions = options.filter((opt) => opt.trim() !== "");
    if (filledOptions.length < 4) {
      setError("Please enter at least 4 options.");
      return;
    }
    const selectedCategoryIds = Object.keys(selectedCategories).filter(
      (id) => selectedCategories[id]
    );
    if (selectedCategoryIds.length === 0) {
      setError("Please select at least one category.");
      return;
    }

    try {
      let vimeoId = null;

      // Handle video upload if changed
      if (isVideoChanged && file) {
        setUploadStatus("initiating");
        setError(null);

        // Initiate new video upload
        const initResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/upload/questions/init`,
          {
            name: file.name,
            file_size: file.size,
          }
        );

        const initData = initResponse.data;
        if (!initResponse.data.success) {
          throw new Error(initData.message || "Failed to initiate upload");
        }

        vimeoId = initData.data.vimeo_id;

        // Upload the new video
        setUploadStatus("uploading");
        setUploading(true);
        setProgress(0);

        await new Promise((resolve, reject) => {
          const upload = new Upload(file, {
            endpoint: initData.data.upload_link,
            uploadUrl: initData.data.upload_link,
            retryDelays: [0, 3000, 5000, 10000],
            chunkSize: 5 * 1024 * 1024, // 5MB chunks
            metadata: {
              filename: btoa(unescape(encodeURIComponent(file.name))),
              filetype: btoa(unescape(encodeURIComponent(file.type))),
            },
            headers: {},
            httpStack: new DefaultHttpStack(),
            onError: (error) => {
              console.error("Upload failed:", error);
              setError(`Upload failed: ${error.message}`);
              setUploadStatus("error");
              setUploading(false);
              reject(error);
            },
            onProgress: (bytesUploaded, bytesTotal) => {
              setProgress((bytesUploaded / bytesTotal) * 100);
            },
            onSuccess: () => {
              resolve();
            },
          });

          upload.options.method = "PATCH";
          upload.start();
        });

        // Delete the old video if it exists
        if (originalVideoUrl) {
          try {
            await axios.delete(
              `${process.env.REACT_APP_API_URL}/api/questions/${questionId}/video`,
              {
                data: { video_url: originalVideoUrl },
              }
            );
          } catch (deleteError) {
            console.error("Failed to delete old video:", deleteError);
            // Continue with update even if old video deletion fails
          }
        }
      }

      // Update the question
      setUploadStatus("updating");

      const updateData = {
        qid: qid,
        question_text: questionText,
        option_1: options[0],
        option_2: options[1],
        option_3: options[2],
        option_4: options[3],
        option_5: options[4],
        option_6: options[5],
        word_1: words[0],
        word_2: words[1],
        word_3: words[2],
        answer: answer,
        parts_of_speech: partsOfSpeech,
        other_info: otherInfo,
        hints: hints,
        priority: priority,
        tense: selectedTense,
        categories: Object.keys(selectedCategories).filter(
          (id) => selectedCategories[id]
        ),
      };

      // Only include vimeoId if we uploaded a new video
      if (isVideoChanged && file && vimeoId) {
        updateData.vimeoId = vimeoId;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/questions/${questionId}`,
        updateData
      );

      setUploadStatus("completed");
      navigate("/xl-admin/questions");
    } catch (err) {
      console.error("Update process error:", err);
      setError(err.response?.data?.message || err.message || "Update failed");
      setUploadStatus("error");
      setUploading(false);
    }
  };

  const getSubcategories = (parentId) => {
    return categories.filter((cat) => cat.parent_id === parentId);
  };

  const isLeafCategory = (categoryId) => {
    return !categories.some((cat) => cat.parent_id === categoryId);
  };

  const renderCategoryTree = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const hasGrandchildren =
      category.grandchildren && category.grandchildren.length > 0;
    const isExpanded = expandedCategories[category.id];
    const isLeaf = !hasChildren && !hasGrandchildren;

    return (
      <div
        key={`cat-${category.id}`}
        className="mb-2"
        style={{ marginLeft: `${level * 16}px` }}
      >
        <div className="d-flex align-items-center">
          {isLeaf ? (
            <>
              <input
                type="checkbox"
                id={`cat-${category.id}`}
                checked={!!selectedCategories[category.id]}
                onChange={() => toggleCategorySelection(category.id)}
                className="form-check-input me-2"
              />
              <label
                htmlFor={`cat-${category.id}`}
                className="form-check-label"
              >
                {category.name}
              </label>
            </>
          ) : (
            <div
              className="d-flex align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => toggleExpand(category.id)}
            >
              <span className="me-2">{isExpanded ? "−" : "+"}</span>
              <span>{category.name}</span>
            </div>
          )}
        </div>

        {isExpanded && (
          <>
            {hasChildren && (
              <div className="mt-2">
                {category.children.map((child) =>
                  renderCategoryTree(child, level + 1)
                )}
              </div>
            )}

            {hasGrandchildren && (
              <div className="mt-2">
                {category.grandchildren.map((grandchild) =>
                  renderCategoryTree(grandchild, level + 1)
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const getButtonText = () => {
    switch (uploadStatus) {
      case "initiating":
        return "Preparing upload...";
      case "uploading":
        return `Uploading... ${Math.round(progress)}%`;
      case "updating":
        return "Updating question...";
      case "completed":
        return "Update Complete";
      case "error":
        return "Try Again";
      default:
        return "Update Question";
    }
  };

  if (loadingQuestion) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Edit Question</h2>

      <div className="row">
        <div className="col-md-8">
          <div className="mb-3">
            <label className="form-label">ID:</label>
            <input
              type="number"
              className="form-control"
              value={qid}
              onChange={(e) => setQid(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Question:</label>
            <textarea
              className="form-control"
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Options</label>
            {options.map((option, index) => (
              <input
                key={`option-${index}`}
                type="text"
                className="form-control mb-2"
                placeholder={`Option ${index + 1}`}
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
              />
            ))}
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Answer</label>
              <select
                className="form-select"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                required
              >
                <option value="">Select answer</option>
                {options.map(
                  (option, index) =>
                    option && (
                      <option key={`answer-${index}`} value={`${index + 1}`}>
                        Option {index + 1}: {option}
                      </option>
                    )
                )}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Parts of Speech</label>
              <input
                type="text"
                className="form-control"
                value={partsOfSpeech}
                onChange={(e) => setPartsOfSpeech(e.target.value)}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">Hints</label>
            <textarea
              className="form-control"
              value={hints}
              onChange={(e) => setHints(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Other Info</label>
            <textarea
              className="form-control"
              value={otherInfo}
              onChange={(e) => setOtherInfo(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Explainer video:</label>
            {originalVideoUrl && !isVideoChanged && (
              <div className="mb-2">
                <div className="alert alert-info">
                  Current video:{" "}
                  <a
                    href={originalVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Video
                  </a>
                </div>
                <button
                  className="btn btn-sm btn-outline-danger mb-2"
                  onClick={() => {
                    setFile(null);
                    setIsVideoChanged(true);
                    setVideoUrl("");
                  }}
                >
                  Change Video
                </button>
              </div>
            )}
            {isVideoChanged && (
              <>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime"
                  className="form-control"
                  onChange={handleFileChange}
                />
                {file && (
                  <small className="text-muted">Selected: {file.name}</small>
                )}
                {uploading && (
                  <div className="progress mt-2">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {progress}%
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          <button
            className="btn btn-lime-green text-white"
            onClick={handleUpdate}
            disabled={
              uploading ||
              uploadStatus === "initiating" ||
              uploadStatus === "updating"
            }
          >
            {getButtonText()}
          </button>
        </div>

        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Categories</h5>
            </div>
            <div
              className="card-body"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {loadingCategories ? (
                <p>Loading categories...</p>
              ) : (
                <div className="category-tree">
                  {Array.isArray(categories) &&
                    categories
                      .filter((cat) => !cat.parent_id)
                      .map((category) => renderCategoryTree(category))}
                </div>
              )}
            </div>
          </div>

          <div className="card mb-3">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => setShowWords(!showWords)}
            >
              <h5 className="mb-0">Own vocabulary</h5>
              <span>{showWords ? "−" : "+"}</span>
            </div>
            {showWords && (
              <div className="card-body">
                {words.map((word, index) => (
                  <input
                    key={`word-${index}`}
                    type="text"
                    className="form-control mb-2"
                    placeholder={`Word ${index + 1}`}
                    value={word}
                    onChange={(e) => handleWordChange(index, e.target.value)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Tense</h5>
            </div>
            <div className="card-body">
              {loadingTenses ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <select
                  className="form-select"
                  value={selectedTense}
                  onChange={(e) => setSelectedTense(e.target.value)}
                >
                  <option value="">Select tense</option>
                  {tenses.map((tenseItem) => (
                    <option key={`tense-${tenseItem.id}`} value={tenseItem.id}>
                      {tenseItem.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <h5 className="mb-0">Priority</h5>
            </div>
            <div className="card-body">
              <select
                className="form-select"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value))}
              >
                {[...Array(11).keys()].map((num) => (
                  <option key={`priority-${num}`} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditQuestionForm;
